import { uiText } from '../../i18n/uiText';
import { useEffect } from 'react';

const DATE_INPUT_LIMITS = {
  date: { min: '1900-01-01', max: '2100-12-31' },
  'datetime-local': { min: '1900-01-01T00:00', max: '2100-12-31T23:59' },
};

function enhanceDateInputs(root = document) {
  root.querySelectorAll('input[type="date"], input[type="datetime-local"]').forEach((element) => {
    const limits = DATE_INPUT_LIMITS[element.type];
    if (!limits) return;
    if (!element.hasAttribute('min')) element.min = limits.min;
    if (!element.hasAttribute('max')) element.max = limits.max;
    if (!element.hasAttribute('lang')) element.lang = document.documentElement.lang || 'ru';
    if (!element.hasAttribute('inputmode')) element.inputMode = 'numeric';
    if (
      element.value
      && element.value >= element.min
      && element.value <= element.max
    ) {
      element.dataset.lastValidDate = element.value;
    }
  });
}

function setNativeInputValue(element, value) {
  const descriptor = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value',
  );
  descriptor?.set?.call(element, value);
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

export function enhanceTables(root = document) {
  root.querySelectorAll('.table-wrap table, .workspace-table-wrap table, .ap-table-wrap table').forEach((table) => {
    const headerRows = Array.from(table.tHead?.rows || []);
    // Complex row/column spans cannot be represented as labelled mobile cards.
    // Keep their original horizontal-scroll table instead of mislabelling cells.
    if (headerRows.length !== 1 || Array.from(headerRows[0].cells).some(cell => cell.colSpan > 1 || cell.rowSpan > 1)
      || table.querySelector('tbody td[rowspan]:not([rowspan="1"])')
      || Array.from(table.querySelectorAll('tbody tr')).some(row => row.cells.length > 1 && Array.from(row.cells).some(cell => cell.colSpan > 1))) {
      table.classList.remove('responsive-table');
      return;
    }
    const headings = Array.from(headerRows[0].cells).map((cell) => cell.textContent.trim());
    if (!headings.length) return;
    table.classList.add('responsive-table');
    table.querySelectorAll('tbody tr').forEach((row) => {
      const cells = Array.from(row.children).filter((cell) => cell.tagName === 'TD');
      if (cells.length === 1 && Number(cells[0].colSpan) > 1) {
        row.classList.add('responsive-table-state-row');
        return;
      }
      row.classList.remove('responsive-table-state-row');
      cells.forEach((cell, index) => {
        if (!cell.dataset.label || cell.dataset.autoLabel === 'true') {
          cell.dataset.label = headings[index] || '';
          cell.dataset.autoLabel = 'true';
        }
      });
    });
  });
}

function enhanceTooltips(root = document) {
  root.querySelectorAll('button[title], a[title], button[aria-label], a[aria-label]').forEach((element) => {
    const title = element.getAttribute('title')?.trim();
    const visibleText = element.textContent?.trim();
    const label = (title || (!visibleText ? element.getAttribute('aria-label') : ''))?.trim();
    if (!label) return;
    element.dataset.tooltip = label;
    if (!element.getAttribute('aria-label') && !element.textContent.trim()) element.setAttribute('aria-label', label);
    if (element.hasAttribute('title')) element.removeAttribute('title');
  });
}

function normaliseAccessibleLabel(value) {
  return String(value || '')
    .replace(/\s*\*\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function enhanceFormAccessibility(root = document) {
  root.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach((element) => {
    if (
      element.getAttribute('aria-label')
      || element.getAttribute('aria-labelledby')
      || element.labels?.length
    ) return;

    let label = '';
    const row = element.closest('tr');
    if (element.type === 'checkbox' && row) {
      const rowText = normaliseAccessibleLabel(row.querySelector('td, th')?.textContent);
      label = rowText ? uiText("Выбрать: {v0}", {v0: (rowText)}) : uiText("Выбрать все строки");
    }

    if (!label) {
      const field = element.closest(
        '.form-group, .form-field, .workspace-form-field, .filter-field, .field',
      );
      const candidates = Array.from(field?.children || []).filter((candidate) => (
        candidate !== element
        && (candidate.tagName === 'LABEL' || candidate.classList?.contains('form-label'))
      ));
      label = normaliseAccessibleLabel(candidates[0]?.textContent);
    }

    if (!label && element.previousElementSibling?.tagName === 'LABEL') {
      label = normaliseAccessibleLabel(element.previousElementSibling.textContent);
    }
    if (!label && element.tagName === 'SELECT') {
      label = normaliseAccessibleLabel(element.options?.[0]?.textContent);
    }
    if (!label) label = normaliseAccessibleLabel(element.getAttribute('placeholder'));
    if (!label && element.type === 'range') label = uiText("Изменить значение");
    if (!label && element.type === 'checkbox') label = uiText("Переключить параметр");
    if (!label && element.tagName === 'TEXTAREA') label = uiText("Текстовое поле");
    if (!label) label = uiText("Поле ввода");

    element.setAttribute('aria-label', label);
  });
}

export default function UiEnhancer() {
  useEffect(() => {
    let frame = 0;
    const enhance = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        enhanceTables();
        enhanceFormAccessibility();
        enhanceTooltips();
        enhanceDateInputs();
      });
    };
    enhance();
    const observer = new MutationObserver(enhance);
    observer.observe(document.body, { childList: true, subtree: true });

    const tooltip = document.createElement('div');
    tooltip.className = 'app-global-tooltip';
    tooltip.setAttribute('role', 'tooltip');
    tooltip.setAttribute('aria-hidden', 'true');
    document.body.appendChild(tooltip);
    let activeTarget = null;

    const hideTooltip = () => {
      activeTarget = null;
      tooltip.classList.remove('is-visible');
      tooltip.setAttribute('aria-hidden', 'true');
    };

    const positionTooltip = () => {
      if (!activeTarget || !document.body.contains(activeTarget)) return hideTooltip();
      const targetRect = activeTarget.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const gap = 8;
      const edge = 10;
      const showBelow = targetRect.top < tooltipRect.height + gap + edge;
      const top = showBelow
        ? targetRect.bottom + gap
        : targetRect.top - tooltipRect.height - gap;
      const centered = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
      const left = Math.max(edge, Math.min(centered, window.innerWidth - tooltipRect.width - edge));
      tooltip.style.left = `${Math.round(left)}px`;
      tooltip.style.top = `${Math.round(Math.max(edge, top))}px`;
      tooltip.dataset.placement = showBelow ? 'bottom' : 'top';
    };

    const showTooltip = (target) => {
      const label = target?.dataset?.tooltip?.trim();
      if (!label) return;
      // Tooltips belonging to a native dialog must live in its top layer.
      const container = target.closest('dialog[open]') || document.body;
      if (tooltip.parentElement !== container) container.appendChild(tooltip);
      activeTarget = target;
      tooltip.textContent = label;
      tooltip.setAttribute('aria-hidden', 'false');
      tooltip.classList.add('is-visible');
      window.requestAnimationFrame(positionTooltip);
    };

    const findTarget = (event) => event.target?.closest?.('[data-tooltip]');
    const handleMouseOver = (event) => {
      const target = findTarget(event);
      if (target && target !== activeTarget) showTooltip(target);
    };
    const handleMouseOut = (event) => {
      if (activeTarget && !activeTarget.contains(event.relatedTarget)) hideTooltip();
    };
    const handleFocusIn = (event) => {
      const target = findTarget(event);
      if (target) showTooltip(target);
    };
    const handleFocusOut = (event) => {
      if (activeTarget && event.target === activeTarget) hideTooltip();
    };
    const handlePointerDown = () => hideTooltip();
    const handleDateInput = (event) => {
      const element = event.target;
      if (!element?.matches?.('input[type="date"], input[type="datetime-local"]')) return;
      const limits = DATE_INPUT_LIMITS[element.type];
      if (!element.value || !limits) {
        element?.setCustomValidity?.('');
        delete element?.dataset?.dateOutOfRange;
        return;
      }
      const outOfRange = element.value < element.min || element.value > element.max;
      element.setCustomValidity(
        outOfRange
          ? uiText("Допустимый диапазон дат: {v0} — {v1}", {v0: (limits.min.slice(0, 10)), v1: (limits.max.slice(0, 10))})
          : '',
      );
      if (outOfRange) element.dataset.dateOutOfRange = 'true';
      else {
        delete element.dataset.dateOutOfRange;
        element.dataset.lastValidDate = element.value;
      }
    };
    const restoreInvalidDate = (event) => {
      const element = event.target;
      if (!element?.matches?.('input[type="date"], input[type="datetime-local"]')) return;
      if (element.dataset.dateOutOfRange !== 'true') return;
      const fallback = element.dataset.lastValidDate || '';
      setNativeInputValue(element, fallback);
      element.setCustomValidity('');
      delete element.dataset.dateOutOfRange;
    };
    const blockInvalidDateSubmission = (event) => {
      const scope = event.target;
      if (!scope?.matches?.('form')) return;
      const invalidDate = scope.querySelector('input[data-date-out-of-range="true"]');
      if (!invalidDate) return;
      event.preventDefault();
      event.stopPropagation();
      invalidDate.focus();
      invalidDate.reportValidity();
    };

    document.addEventListener('input', handleDateInput, true);
    document.addEventListener('change', handleDateInput, true);
    document.addEventListener('change', restoreInvalidDate, true);
    document.addEventListener('blur', restoreInvalidDate, true);
    document.addEventListener('submit', blockInvalidDateSubmission, true);
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('click', handlePointerDown);
    window.addEventListener('scroll', positionTooltip, true);
    window.addEventListener('resize', positionTooltip);
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      document.removeEventListener('input', handleDateInput, true);
      document.removeEventListener('change', handleDateInput, true);
      document.removeEventListener('change', restoreInvalidDate, true);
      document.removeEventListener('blur', restoreInvalidDate, true);
      document.removeEventListener('submit', blockInvalidDateSubmission, true);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('click', handlePointerDown);
      window.removeEventListener('scroll', positionTooltip, true);
      window.removeEventListener('resize', positionTooltip);
      tooltip.remove();
    };
  }, []);
  return null;
}
